/**
 * Shared hook that loads the core salary datasets (employees, roles,
 * shifts, holidays) used by multiple tabs.
 */
import { useCallback, useEffect, useState } from 'react';
import useRequest from '../../hooks/useRequest';
import type {
    SalaryEmployee,
    SalaryHoliday,
    SalaryRole,
    SalaryShift,
} from '../../domain/salary/types';

interface ApiResponse<T> {
    success: boolean;
    data: T;
}

export interface SalaryCoreData {
    employees: SalaryEmployee[];
    roles: SalaryRole[];
    shifts: SalaryShift[];
    holidays: SalaryHoliday[];
    loading: boolean;
    reload: () => Promise<void>;
}

export function useSalaryCoreData(): SalaryCoreData {
    const request = useRequest();
    const [employees, setEmployees] = useState<SalaryEmployee[]>([]);
    const [roles, setRoles] = useState<SalaryRole[]>([]);
    const [shifts, setShifts] = useState<SalaryShift[]>([]);
    const [holidays, setHolidays] = useState<SalaryHoliday[]>([]);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [employeesRes, rolesRes, shiftsRes, holidaysRes] = await Promise.all([
                request<ApiResponse<SalaryEmployee[]>>({ path: '/salary/employees' }),
                request<ApiResponse<SalaryRole[]>>({ path: '/salary/roles' }),
                request<ApiResponse<SalaryShift[]>>({ path: '/salary/shifts' }),
                request<ApiResponse<SalaryHoliday[]>>({ path: '/salary/holidays' }),
            ]);
            setEmployees(employeesRes.data ?? []);
            setRoles(rolesRes.data ?? []);
            setShifts(shiftsRes.data ?? []);
            setHolidays(holidaysRes.data ?? []);
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => {
        reload();
    }, [reload]);

    return { employees, roles, shifts, holidays, loading, reload };
}
